import { APP_BUILD_INFO } from '../buildInfo'
import { createLightSyncPacing } from './lightSyncPacing'
import {
   mergeRemoteHistoryEntries as mergeHistoryLists,
   shouldKeepLocalCurrentFast,
   type RemoteFastEntry as FastEntry,
} from './remoteMerge'

type HistoryRemoteState = {
   monthsLoaded: number
   loadingPromise: Promise<void> | null
   error: unknown
   lastFetch: number
}

type SyncPersistMeta = Record<string, unknown> & {
   serverUpdatedAt?: number
}

type SyncDebugInfo = {
   lastPersist: SyncPersistMeta | null
   serverBuildInfo?: unknown
}

type RemoteSyncRuntimeDeps = {
   canUseRemoteSync: () => boolean
   buildHashedUserId: () => Promise<string>
   buildRequestSnapshot: () => {
      currentFast: unknown
      fastHistory: unknown[]
      heightCm: number | null
      targetWeightKg: number | null
      targetMonthlyRunEffort: number | null
      preferredLocale?: string | null
      unitSystem?: 'metric' | 'imperial'
      weightChartMode: 'line' | 'scatter'
   }
   clearSyncFailureState: () => void
   currentStateVersion: () => number
   deleteRemoteHistoryEntry?: (historyId: number) => Promise<void>
   deserializeFastEntry: (entry: unknown) => FastEntry | null
   ensureCurrentFastDurationUpdatedAt: (
      entry: FastEntry,
      fallbackTimestamp: number,
   ) => void
   getActiveTab: () => string
   getCurrentFast: () => FastEntry | null
   getFastHistory: () => FastEntry[]
   getHistoryRemoteState: () => HistoryRemoteState
   getLatestLocalDurationChangeAt: () => number
   getLightweightSyncInFlight: () => boolean
   getRemoteLoadInProgress: () => boolean
   getSyncDebugInfo: () => SyncDebugInfo
   getTargetUserId: () => string
   getUserIdentity: () => { userId: string }
   getUserProfile: () => {
      heightCm: number | null
      targetWeightKg: number | null
      targetMonthlyRunEffort: number | null
      preferredLocale: string | null
      unitSystem: 'metric' | 'imperial'
   }
   historyEndpoint: string
   historySummaryCacheClear: () => void
   initialRemoteHydrationPending: () => boolean
   lightSyncIntervalMs: number
   loadRemoteStateFallback?: (options?: Record<string, unknown>) => Promise<unknown>
   markStateChanged: () => void
   normalizeDisplayModePreference: (value: unknown) => 'elapsed' | 'remaining' | null
   parseTimestampToNumber: (value: unknown) => number | null
   persistDebugInfo: () => void
   prepareFastEntryForPersistence: (entry: FastEntry) => Record<string, unknown>
   saveState: () => void
   sanitizeLastPersistMeta: (value: unknown) => SyncPersistMeta | null
   setCurrentFast: (value: FastEntry | null) => void
   setFastCompletionNotificationInFlight: (value: boolean) => void
   setFastHistory: (value: FastEntry[]) => void
   setHistoryRemoteState: (updater: (state: HistoryRemoteState) => void) => void
   setInitialRemoteHydrationPending: (value: boolean) => void
   setLatestLocalDurationChangeAt: (value: number) => void
   setLightweightSyncInFlight: (value: boolean) => void
   setLightweightSyncTimerId: (value: number | null) => void
   getLightweightSyncTimerId: () => number | null
   setRemoteLoadInProgress: (value: boolean) => void
   setSolidWeightChartMode: (mode: 'line' | 'scatter', options: { emit: boolean }) => void
   sortFastHistoryDescending: () => void
   startLightweightSyncLoopFallback?: () => void
   stopLightweightSyncLoopFallback?: () => void
   switchTab: (
      tab: 'current' | 'history' | 'habits' | 'measurements' | 'account' | undefined,
      options?: { suppressHistoryUpdate?: boolean },
   ) => void
   syncAccountStateToSolid: () => void
   syncCurrentFastDurationPatchFallback?: (
      options?: Record<string, unknown>,
   ) => Promise<unknown>
   syncCurrentFastStateToSolid: () => void
   syncHistoryStateToSolid: () => void
   syncHabitsStateToSolid: () => void
   /**
    * Syncs the measurement, weight and habit collections, which keep their own
    * delta cursors. With `all: false` only collections with local work (a first
    * pull or unpushed edits) sync; the lightweight tick passes that when the
    * change stamp says nothing moved on the server.
    */
   syncDeltaCollections?: (options: { all: boolean }) => void
   syncState: (action: string) => Promise<unknown>
   syncWeightStateToSolid: () => void
   syncEndpoint: string
   upsertRemoteHistoryEntry: (entry: FastEntry) => Promise<unknown>
   updateHistorySummary: (options?: Record<string, unknown>) => Promise<unknown>
   updateUI: () => void
   applyUnitSystemPreference: (
      value: unknown,
      options: { persist: boolean; sync: boolean; markChange: boolean },
   ) => void
   applyLocalePreference: (
      locale: unknown,
      options: {
         persist: boolean
         sync: boolean
         markChange: boolean
         refresh: boolean
      },
   ) => void
}

/**
 * Resolves the hashed sync user id, falling back to the raw identity.
 *
 * @param deps - The runtime dependencies.
 * @returns The trimmed hashed id, or an empty string.
 */
async function resolveHashedUserId(deps: RemoteSyncRuntimeDeps) {
   const fallback = deps.getUserIdentity().userId
   try {
      return ((await deps.buildHashedUserId()) || fallback).trim()
   } catch (error) {
      console.error('Failed to hash user ID for load', error)
      return fallback.trim()
   }
}

/**
 * Records the server's build info and last-persist metadata from a sync
 * response into the debug info.
 *
 * @param deps - The runtime dependencies.
 * @param body - The parsed response body.
 * @param options - Whether to persist even when the metadata is null.
 */
function recordServerMeta(
   deps: RemoteSyncRuntimeDeps,
   body: Record<string, unknown>,
   options = { persistNull: false },
) {
   if (body.buildInfo && typeof body.buildInfo === 'object') {
      deps.getSyncDebugInfo().serverBuildInfo = body.buildInfo
   }
   const serverMeta = deps.sanitizeLastPersistMeta(body.lastPersistMeta)
   if (serverMeta || options.persistNull) {
      deps.getSyncDebugInfo().lastPersist = serverMeta
      deps.persistDebugInfo()
   }
}

/**
 * Merges remote fast-history entries with local ones, de-duplicating by id and start/end range.
 *
 * @param deps - The runtime dependencies.
 * @param remoteEntries - The raw remote history entries.
 */
function mergeRemoteHistoryEntries(
   deps: RemoteSyncRuntimeDeps,
   remoteEntries: unknown[],
) {
   if (!Array.isArray(remoteEntries)) {
      return
   }
   const normalizedRemote = remoteEntries
      .map(entry => deps.deserializeFastEntry(entry))
      .filter((entry): entry is FastEntry => entry !== null)
   deps.setFastHistory(mergeHistoryLists(deps.getFastHistory(), normalizedRemote))
   deps.sortFastHistoryDescending()
   deps.saveState()
}

/**
 * Uploads local fast-history entries that lack a server id.
 */
async function syncPendingHistoryEntries(deps: RemoteSyncRuntimeDeps) {
   if (!deps.getUserIdentity().userId || !deps.canUseRemoteSync()) {
      return
   }
   const pending = deps
      .getFastHistory()
      .filter(entry => entry && typeof entry === 'object' && !entry.historyId)
   if (!pending.length) {
      return
   }

   for (const entry of pending) {
      try {
         await deps.upsertRemoteHistoryEntry(entry)
      } catch (error) {
         console.error('Failed to sync pending history entry', error)
         break
      }
   }

   deps.saveState()
   deps.historySummaryCacheClear()
}

/**
 * Loads remote fast history for the last N months and merges it locally.
 *
 * @param deps - The runtime dependencies.
 * @param months - Number of months to load (defaults to 1).
 */
async function loadRemoteHistoryForMonths(deps: RemoteSyncRuntimeDeps, months = 1) {
   const userIdentity = deps.getUserIdentity()
   if (!userIdentity.userId || !deps.canUseRemoteSync()) {
      return
   }

   const requiredMonths = Math.max(1, Number.isFinite(months) ? months : 1)
   let hashedId = await deps.buildHashedUserId()
   if (!hashedId) {
      hashedId = userIdentity.userId
   }
   if (!hashedId) {
      return
   }

   deps.setHistoryRemoteState(state => {
      state.error = null
   })
   const params = new URLSearchParams({
      userId: hashedId,
      months: String(requiredMonths),
   })
   const response = await fetch(`${deps.historyEndpoint}?${params.toString()}`, {
      cache: 'no-store',
      method: 'GET',
   })
   if (!response.ok) {
      throw new Error(`History fetch failed with status ${response.status}`)
   }

   const data = await response.json()
   const remoteEntries =
      Array.isArray(data.fastHistory) ?
         data.fastHistory
            .map((entry: unknown) => deps.deserializeFastEntry(entry))
            .filter(Boolean)
      :  []

   mergeRemoteHistoryEntries(deps, remoteEntries)
   deps.setHistoryRemoteState(state => {
      state.monthsLoaded = requiredMonths
      state.lastFetch = Date.now()
   })
   await syncPendingHistoryEntries(deps)
   deps.historySummaryCacheClear()
}

/**
 * Ensures remote history for at least N months is loaded, coalescing concurrent requests.
 *
 * @param deps - The runtime dependencies.
 * @param months - Number of months required (defaults to 1).
 */
async function ensureRemoteHistoryForMonths(deps: RemoteSyncRuntimeDeps, months = 1) {
   const userIdentity = deps.getUserIdentity()
   const historyRemoteState = deps.getHistoryRemoteState()
   if (!userIdentity.userId || !deps.canUseRemoteSync()) {
      return
   }

   const requiredMonths = Math.max(1, Number.isFinite(months) ? months : 1)
   if (historyRemoteState.monthsLoaded >= requiredMonths && !historyRemoteState.error) {
      return
   }
   if (historyRemoteState.loadingPromise) {
      try {
         await historyRemoteState.loadingPromise
      } catch {
         // ignored
      }
   }

   const loadPromise = loadRemoteHistoryForMonths(deps, requiredMonths)
      .catch(error => {
         deps.setHistoryRemoteState(state => {
            state.error = error
         })
         const errorMessage =
            error instanceof Error && typeof error.message === 'string' ?
               error.message
            :  ''
         const isFetchAvailabilityIssue =
            errorMessage === 'Failed to fetch' ||
            errorMessage.toLowerCase().includes('fetch')
         if (!isFetchAvailabilityIssue) {
            console.error('Failed to load remote history', error)
         }
      })
      .finally(() => {
         deps.setHistoryRemoteState(state => {
            state.loadingPromise = null
         })
      }) as Promise<void>

   deps.setHistoryRemoteState(state => {
      state.loadingPromise = loadPromise
   })
   await loadPromise
}

/**
 * Fetches the remote state snapshot.
 *
 * @param deps - The runtime dependencies.
 * @param hashedId - The hashed sync user id.
 * @param ifUpdatedSince - Optional last-known server timestamp for an up-to-date short-circuit.
 * @returns The parsed body, or null when the request failed.
 */
async function fetchRemoteState(
   deps: RemoteSyncRuntimeDeps,
   hashedId: string,
   ifUpdatedSince: unknown,
) {
   // Weights and habits sync through their own delta routes, so skip loading them here.
   const requestParams = new URLSearchParams({ userId: hashedId, fields: 'currentFast' })
   if (typeof ifUpdatedSince === 'number' || typeof ifUpdatedSince === 'string') {
      requestParams.set('ifUpdatedSince', String(ifUpdatedSince))
   }
   const response = await fetch(`${deps.syncEndpoint}?${requestParams.toString()}`, {
      cache: 'no-store',
      method: 'GET',
   })
   if (!response.ok) {
      console.error('Failed to load remote state', response.status, response.statusText)
      return null
   }
   const rawData = await response.json()
   return rawData && typeof rawData === 'object' ?
         (rawData as Record<string, unknown>)
      :  {}
}

/**
 * Replaces the local current fast with the remote one unless the local
 * duration edit is newer, then applies the remote display mode.
 *
 * @param deps - The runtime dependencies.
 * @param remoteData - The remote state payload.
 */
function applyRemoteCurrentFast(
   deps: RemoteSyncRuntimeDeps,
   remoteData: Record<string, unknown>,
) {
   const lastPersistMeta = remoteData.lastPersistMeta as Record<string, unknown> | null
   const remoteCurrentFast = deps.deserializeFastEntry(remoteData.currentFast ?? null)
   if (remoteCurrentFast) {
      deps.ensureCurrentFastDurationUpdatedAt(
         remoteCurrentFast,
         deps.parseTimestampToNumber(lastPersistMeta?.clientTimestamp) || Date.now(),
      )
   }
   const localCurrentFast = deps.getCurrentFast()
   const currentFast =
      shouldKeepLocalCurrentFast(localCurrentFast, remoteCurrentFast) ? localCurrentFast
      :  remoteCurrentFast
   const remoteDisplayMode = deps.normalizeDisplayModePreference(remoteData.displayMode)
   if (currentFast && remoteDisplayMode) {
      currentFast.displayMode = remoteDisplayMode
   }
   deps.setCurrentFast(currentFast)
   if (currentFast) {
      deps.ensureCurrentFastDurationUpdatedAt(
         currentFast,
         Number.isFinite(currentFast.startTime) ? currentFast.startTime : Date.now(),
      )
      deps.setLatestLocalDurationChangeAt(
         Math.max(
            deps.getLatestLocalDurationChangeAt(),
            currentFast.durationUpdatedAt ?? currentFast.startTime,
         ),
      )
   }
}

/**
 * Merges the remote fast history into local state.
 *
 * @param deps - The runtime dependencies.
 * @param remoteData - The remote state payload.
 */
function applyRemoteCollections(
   deps: RemoteSyncRuntimeDeps,
   remoteData: Record<string, unknown>,
) {
   const remoteHistory = (
      Array.isArray(remoteData.fastHistory) ?
         remoteData.fastHistory
      :  [])
      .map(entry => deps.deserializeFastEntry(entry))
      .filter((entry): entry is FastEntry => entry !== null)
   mergeRemoteHistoryEntries(deps, remoteHistory)
   deps.setHistoryRemoteState(state => {
      state.error = null
      state.loadingPromise = null
      state.lastFetch = Date.now()
      state.monthsLoaded = remoteHistory.length ? 1 : 0
   })
   deps.historySummaryCacheClear()
}

/**
 * Applies remote profile fields (targets, chart mode, locale, units) without
 * re-syncing them.
 *
 * @param deps - The runtime dependencies.
 * @param remoteData - The remote state payload.
 */
function applyRemoteProfile(
   deps: RemoteSyncRuntimeDeps,
   remoteData: Record<string, unknown>,
) {
   const profile = deps.getUserProfile()
   const has = (key: string) => Object.prototype.hasOwnProperty.call(remoteData, key)
   const numberOrNull = (value: unknown) => (typeof value === 'number' ? value : null)
   if (has('heightCm')) {
      profile.heightCm = numberOrNull(remoteData.heightCm)
   }
   if (has('targetWeightKg')) {
      profile.targetWeightKg = numberOrNull(remoteData.targetWeightKg)
   }
   if (has('targetMonthlyRunEffort')) {
      profile.targetMonthlyRunEffort = numberOrNull(remoteData.targetMonthlyRunEffort)
   }
   if (
      remoteData.weightChartMode === 'line' ||
      remoteData.weightChartMode === 'scatter'
   ) {
      deps.setSolidWeightChartMode(remoteData.weightChartMode, { emit: false })
   }
   if (has('preferredLocale')) {
      deps.applyLocalePreference(remoteData.preferredLocale, {
         persist: false,
         sync: false,
         markChange: false,
         refresh: false,
      })
   }
   if (remoteData.unitSystem === 'metric' || remoteData.unitSystem === 'imperial') {
      deps.applyUnitSystemPreference(remoteData.unitSystem, {
         persist: false,
         sync: false,
         markChange: false,
      })
   }
}

/**
 * Pushes every slice of local state to the Solid stores after a remote load.
 */
function publishStateToSolid(deps: RemoteSyncRuntimeDeps) {
   deps.markStateChanged()
   deps.saveState()
   deps.updateUI()
   deps.syncCurrentFastStateToSolid()
   deps.syncHistoryStateToSolid()
   deps.syncWeightStateToSolid()
   deps.syncHabitsStateToSolid()
   deps.syncAccountStateToSolid()
}

/**
 * Applies a full remote snapshot: current fast, collections, profile, then
 * publishes to the Solid stores.
 *
 * @param deps - The runtime dependencies.
 * @param remoteData - The remote state payload.
 */
async function applyRemoteState(
   deps: RemoteSyncRuntimeDeps,
   remoteData: Record<string, unknown>,
) {
   applyRemoteCurrentFast(deps, remoteData)
   applyRemoteCollections(deps, remoteData)
   await syncPendingHistoryEntries(deps)
   deps.setFastCompletionNotificationInFlight(false)
   applyRemoteProfile(deps, remoteData)
   recordServerMeta(deps, remoteData, { persistNull: true })
   publishStateToSolid(deps)
}

/**
 * Builds the PATCH body that syncs only the current fast and profile scalars.
 *
 * @param deps - The runtime dependencies.
 * @param currentFast - The local current fast.
 * @param currentFastSnapshot - The serialized current fast.
 * @param hashedId - The hashed sync user id.
 * @param trigger - The sync action name.
 * @returns The request body.
 */
function buildCurrentFastPatchBody(
   deps: RemoteSyncRuntimeDeps,
   currentFast: FastEntry,
   currentFastSnapshot: Record<string, unknown>,
   hashedId: string,
   trigger: string,
) {
   const snapshot = deps.buildRequestSnapshot()
   return {
      userId: hashedId,
      action: trigger,
      currentFast: currentFastSnapshot,
      clientBuildInfo: APP_BUILD_INFO,
      heightCm: snapshot.heightCm,
      targetWeightKg: snapshot.targetWeightKg,
      targetMonthlyRunEffort: snapshot.targetMonthlyRunEffort,
      preferredLocale: snapshot.preferredLocale ?? null,
      unitSystem: snapshot.unitSystem ?? null,
      displayModePreference: deps.normalizeDisplayModePreference(currentFast.displayMode),
      weightChartMode: snapshot.weightChartMode,
      clientTimestamp: Date.now(),
      clientStateVersion: deps.currentStateVersion(),
      lastPersistMeta: deps.getSyncDebugInfo().lastPersist ?? null,
   }
}

/**
 * Reads a JSON response body, or null when the response is not JSON.
 *
 * @param response - The fetch response.
 * @returns The parsed body, or null.
 */
async function readJsonBody(response: Response) {
   const contentType = response.headers.get('content-type') || ''
   return contentType.includes('application/json') ?
         ((await response.json()) as Record<string, unknown> | null)
      :  null
}

/**
 * Sends a lightweight PATCH to sync only the current fast's duration/details.
 *
 * @param deps - The runtime dependencies.
 * @param options - Optional overrides (e.g. `trigger`).
 * @returns True on success, false when skipped or on conflict.
 */
async function syncCurrentFastDurationPatch(
   deps: RemoteSyncRuntimeDeps,
   options: Record<string, unknown> = {},
) {
   const currentFast = deps.getCurrentFast()
   if (!currentFast || !deps.getUserIdentity().userId || !deps.canUseRemoteSync()) {
      return false
   }
   const trigger =
      typeof options.trigger === 'string' ? options.trigger : 'duration-adjust'
   const currentFastSnapshot = deps.prepareFastEntryForPersistence(currentFast)
   const hashedId = await resolveHashedUserId(deps)
   if (!currentFastSnapshot || !hashedId) {
      return false
   }

   const response = await fetch(deps.syncEndpoint, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
         buildCurrentFastPatchBody(
            deps,
            currentFast,
            currentFastSnapshot,
            hashedId,
            trigger,
         ),
      ),
   })
   const responseBody = await readJsonBody(response)
   if (!response.ok) {
      if (
         response.status === 409 &&
         responseBody?.conflict === true &&
         deps.loadRemoteStateFallback
      ) {
         await deps.loadRemoteStateFallback()
         return false
      }
      throw new Error(`PATCH sync failed with ${response.status} ${response.statusText}`)
   }
   recordServerMeta(deps, responseBody ?? {})
   return true
}

/**
 * Creates the lightweight background polling loop.
 *
 * @param deps - The runtime dependencies.
 * @param loadRemoteState - Loads the remote state (late-bound to break the cycle).
 * @returns The loop controls.
 */
function createLightweightSyncLoop(
   deps: RemoteSyncRuntimeDeps,
   loadRemoteState: (options?: Record<string, unknown>) => Promise<unknown>,
) {
   const pacing = createLightSyncPacing()
   let activityListenersInstalled = false
   /**
    * Stops the lightweight background sync interval.
    */
   function stopLightweightSyncLoop() {
      const timerId = deps.getLightweightSyncTimerId()
      if (timerId !== null) {
         window.clearInterval(timerId)
         deps.setLightweightSyncTimerId(null)
      }
      deps.setLightweightSyncInFlight(false)
   }

   /**
    * Runs one lightweight background sync tick when the tab is visible and idle.
    */
   async function runLightweightSync() {
      if (deps.getLightweightSyncInFlight()) {
         return
      }
      if (!deps.getUserIdentity().userId || !deps.canUseRemoteSync()) {
         stopLightweightSyncLoop()
         return
      }
      if (document.visibilityState === 'hidden' || deps.getRemoteLoadInProgress()) {
         return
      }
      if (!pacing.takeTick()) {
         return
      }

      const lastPersist = deps.getSyncDebugInfo().lastPersist
      const lastServerTs =
         typeof lastPersist?.serverUpdatedAt === 'number' ?
            lastPersist.serverUpdatedAt
         :  null

      deps.setLightweightSyncInFlight(true)
      try {
         const result = (await loadRemoteState({
            ifUpdatedSince: lastServerTs,
            skipTabRefresh: true,
         })) as { upToDate?: boolean } | undefined
         deps.syncDeltaCollections?.({
            all: pacing.shouldPullAll(result?.upToDate === true),
         })
      } catch (error) {
         console.error('Lightweight sync failed', error)
      } finally {
         deps.setLightweightSyncInFlight(false)
      }
   }

   /**
    * Records user input and showing the tab as activity, syncing right away
    * when the user comes back from idle.
    */
   function installActivityListeners() {
      if (activityListenersInstalled) {
         return
      }
      activityListenersInstalled = true
      /**
       * Marks activity while the tab is visible, syncing at once after idle.
       */
      const onActivity = () => {
         if (document.visibilityState !== 'hidden' && pacing.markActivity()) {
            void runLightweightSync()
         }
      }
      for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart']) {
         window.addEventListener(type, onActivity, { passive: true, capture: true })
      }
      document.addEventListener('visibilitychange', onActivity)
   }

   /**
    * Starts the lightweight background sync interval and kicks off an immediate tick when stale.
    */
   function startLightweightSyncLoop() {
      if (!deps.getUserIdentity().userId || !deps.canUseRemoteSync()) {
         stopLightweightSyncLoop()
         return
      }
      if (deps.getLightweightSyncTimerId() !== null) {
         return
      }
      installActivityListeners()
      deps.setLightweightSyncTimerId(
         window.setInterval(() => {
            void runLightweightSync()
         }, deps.lightSyncIntervalMs),
      )
      const historyRemoteState = deps.getHistoryRemoteState()
      const hasRecentFetch =
         Date.now() - historyRemoteState.lastFetch < deps.lightSyncIntervalMs
      const hasServerMeta =
         typeof deps.getSyncDebugInfo().lastPersist?.serverUpdatedAt === 'number'
      if (!hasServerMeta || !hasRecentFetch) {
         void runLightweightSync()
      }
   }

   return { runLightweightSync, startLightweightSyncLoop, stopLightweightSyncLoop }
}

/**
 * Creates the remote-sync runtime: full-state hydration, lightweight polling, and remote history loading.
 *
 * @param deps - Injected state accessors, setters, endpoints, and callbacks.
 * @returns The remote-sync runtime methods.
 */
export function createRemoteSyncRuntime(deps: RemoteSyncRuntimeDeps) {
   const loop = createLightweightSyncLoop(deps, options => loadRemoteState(options))

   /**
    * Loads and merges the full remote state (current fast, history, weights, habits, profile).
    *
    * @param options - Optional load hints (e.g. `ifUpdatedSince`, `skipTabRefresh`).
    * @returns An up-to-date marker, or undefined.
    */
   async function loadRemoteState(options: Record<string, unknown> = {}) {
      const isInitialHydration = deps.initialRemoteHydrationPending()
      /**
       * Finishes the initial hydration pass, refreshing the UI once (idempotent).
       */
      const clearInitialHydration = () => {
         if (!isInitialHydration) {
            return
         }
         deps.setInitialRemoteHydrationPending(false)
         deps.updateUI()
         deps.syncCurrentFastStateToSolid()
      }
      const targetUserId = deps.getUserIdentity().userId
      if (!targetUserId) {
         clearInitialHydration()
         return
      }
      if (!deps.canUseRemoteSync()) {
         loop.stopLightweightSyncLoop()
         deps.setRemoteLoadInProgress(false)
         clearInitialHydration()
         return
      }

      const requestVersion = deps.currentStateVersion()
      const hashedId = (await resolveHashedUserId(deps)) || targetUserId
      try {
         deps.setRemoteLoadInProgress(true)
         const remoteData = await fetchRemoteState(deps, hashedId, options.ifUpdatedSince)
         if (!remoteData) {
            return
         }
         if (remoteData.upToDate === true) {
            recordServerMeta(deps, remoteData)
            deps.setHistoryRemoteState(state => {
               state.lastFetch = Date.now()
               state.error = null
            })
            return { upToDate: true }
         }
         // The user signed out or edited locally while the request was in flight.
         if (
            deps.getUserIdentity().userId !== targetUserId ||
            deps.currentStateVersion() !== requestVersion
         ) {
            return
         }
         await applyRemoteState(deps, remoteData)
      } catch (error) {
         console.error('Failed to fetch remote state', error)
      } finally {
         deps.setRemoteLoadInProgress(false)
         clearInitialHydration()
         if (deps.canUseRemoteSync()) {
            loop.startLightweightSyncLoop()
         }
      }
   }

   return {
      ...loop,
      ensureRemoteHistoryForMonths: (months?: number) =>
         ensureRemoteHistoryForMonths(deps, months),
      loadRemoteHistoryForMonths: (months?: number) =>
         loadRemoteHistoryForMonths(deps, months),
      loadRemoteState,
      mergeRemoteHistoryEntries: (remoteEntries: unknown[]) =>
         mergeRemoteHistoryEntries(deps, remoteEntries),
      syncCurrentFastDurationPatch: (options?: Record<string, unknown>) =>
         syncCurrentFastDurationPatch(deps, options),
      syncPendingHistoryEntries: () => syncPendingHistoryEntries(deps),
   }
}
